import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
export interface NumberLineProps {
  start: number;
  end: number;
  highlight?: number[];
  step?: number;
  label?: string;
  mode?: 'stimulus' | 'support';
  /** Optional custom labels for each tick mark (overrides auto-generated labels) */
  tickLabels?: string[];
  /** Optional map from decimal value to display label (e.g., { "0.25": "¼", "0.5": "½" }) */
  fractionLabels?: Record<string, string>;
}
function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}
function formatTick(value: number, maxFractionDigits: number) {
  // Use Dutch locale for comma decimal separator
  const nf = new Intl.NumberFormat('nl-NL', {
    maximumFractionDigits: maxFractionDigits,
    minimumFractionDigits: 0,
    notation: 'standard'
  });
  return nf.format(value);
}
function approxIncludes(values: number[], target: number, epsilon: number) {
  for (const v of values) {
    if (Math.abs(v - target) <= epsilon) return true;
  }
  return false;
}
/**
 * Calculate a "nice" step size for a given range.
 * Produces steps like 0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50, etc.
 */
function calculateNiceStep(range: number): number {
  if (range === 0) return 1;
  // Target ~10 steps
  const rawStep = range / 10;
  // Find the magnitude (e.g., 0.01, 1, 100)
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalizedStep = rawStep / magnitude; // Will be between 1 and 10
  let niceStep: number;
  if (normalizedStep < 1.5) niceStep = 1;else if (normalizedStep < 3.5) niceStep = 2;else if (normalizedStep < 7.5) niceStep = 5;else niceStep = 10;
  // Fix floating point precision issues (e.g., 0.2 * 0.1 = 0.020000000000000004)
  return Number((niceStep * magnitude).toPrecision(10));
}
/**
 * Look up a display label for a tick value.
 * Checks fractionLabels map with approximate matching, falls back to formatTick.
 */
function getTickLabel(value: number, maxFractionDigits: number, tickLabels: string[] | undefined, tickIndex: number, fractionLabels: Record<string, string> | undefined): string {
  // 1. If explicit tickLabels array is provided, use it
  if (tickLabels && tickIndex < tickLabels.length) {
    return tickLabels[tickIndex];
  }
  // 2. If fractionLabels map is provided, look up by approximate value
  if (fractionLabels) {
    for (const [key, label] of Object.entries(fractionLabels)) {
      const keyNum = parseFloat(key);
      if (!isNaN(keyNum) && Math.abs(keyNum - value) < 0.001) {
        return label;
      }
    }
  }
  // 3. Default: use numeric format (nl-NL)
  return formatTick(value, maxFractionDigits);
}
/**
 * Enhanced NumberLine with didactic place value support
 * - Clean, professional UI
 * - Exact positioning
 * - Educational explanations
 * - Robust auto-step algorithm
 */
export function NumberLine({
  start,
  end,
  highlight = [],
  step,
  label,
  mode = 'support',
  tickLabels,
  fractionLabels
}: NumberLineProps) {
  const computed = useMemo(() => {
    const validInputs = isFiniteNumber(start) && isFiniteNumber(end);
    if (!validInputs) return null;
    const min = Math.min(start, end);
    const max = Math.max(start, end);
    const range = max - min;
    // Calculate steps: use explicit step prop if provided, otherwise auto-detect
    let stepSize: number;
    if (isFiniteNumber(step) && step > 0 && range > 0) {
      stepSize = step;
    } else if (range > 0) {
      stepSize = calculateNiceStep(range);
    } else {
      stepSize = 1;
    }
    // Ensure we don't create an infinite loop or massive array
    let steps = range === 0 ? 0 : Math.round(range / stepSize);
    if (steps > 50) {
      // Fallback if step size is too small for the range
      stepSize = calculateNiceStep(range);
      steps = Math.round(range / stepSize);
    }
    const maxFractionDigits = (() => {
      if (stepSize >= 1) return 0;
      if (stepSize >= 0.1) return 1;
      if (stepSize >= 0.01) return 2;
      if (stepSize >= 0.001) return 3;
      return 6;
    })();
    const epsilon = Math.max(stepSize / 2, Math.pow(10, -maxFractionDigits) / 2);
    const ticks = range !== 0 ? Array.from({
      length: steps + 1
    }, (_, i) => Number((min + i * stepSize).toPrecision(10))) : [];
    return {
      min,
      max,
      range,
      steps,
      stepSize,
      maxFractionDigits,
      epsilon,
      ticks
    };
  }, [start, end, step]);
  const cleanedHighlights = useMemo(() => highlight.filter(isFiniteNumber), [highlight]);
  if (!computed) {
    return <div className="bg-red-50 p-6 rounded-2xl border-2 border-red-200 text-center" data-id="element-9643">
        <p className="text-red-600 font-medium" data-id="element-9644">
          Ongeldige getallenlijn configuratie
        </p>
      </div>;
  }
  if (computed.range === 0) {
    return <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl border-2 border-blue-200" aria-label="Getallenlijn met één waarde" data-id="element-9645">
        {label && <div className="text-sm font-bold text-blue-900 mb-4 text-center" data-id="element-9646">
            {label}
          </div>}
        <div className="text-center text-slate-700 text-2xl font-bold" data-id="element-9647">
          {formatTick(computed.min, 6)}
        </div>
      </div>;
  }
  // Helper to get display label for a tick
  const labelForTick = (value: number, index: number) => getTickLabel(value, computed.maxFractionDigits, tickLabels, index, fractionLabels);
  // Context-aware explanation logic
  const isNegative = computed.min < 0;
  const isDecimal = computed.stepSize % 1 !== 0;
  let explanationTitle = 'Wat zie je hier?';
  let explanationText = `Deze getallenlijn loopt van ${labelForTick(computed.min, 0)} tot ${labelForTick(computed.max, computed.steps)}. Elk streepje is ${formatTick(computed.stepSize, computed.maxFractionDigits)} verder.`;
  if (isNegative) {
    explanationTitle = 'Getallen onder nul';
    explanationText = `Deze getallenlijn gaat onder nul. Hoe verder naar links, hoe kleiner het getal. Elk streepje is ${formatTick(computed.stepSize, computed.maxFractionDigits)} verder.`;
  } else if (isDecimal) {
    explanationTitle = 'Kommagetallen';
    explanationText = `Deze getallenlijn zoomt in op de stukjes tussen hele getallen. Elk streepje is ${formatTick(computed.stepSize, computed.maxFractionDigits)} verder.`;
  }
  // ── Shared: number line visualization (used in both modes) ────────────────
  const numberLineViz = <div className="relative h-28" aria-label="Getallenlijn" data-id="element-9648">
      {/* Main Line */}
      <div className="absolute top-1/2 left-0 right-0 h-2.5 bg-gradient-to-r from-blue-400 via-indigo-500 to-blue-400 rounded-full shadow-md -translate-y-1/2" data-id="element-9649" />

      {/* Start Endpoint */}
      <div className="absolute top-1/2 left-0 -translate-y-1/2 -translate-x-1/2 z-10" data-id="element-9650">
        <div className="w-5 h-5 rounded-full bg-blue-600 border-2 border-white shadow-lg" data-id="element-9651" />
        <div className="absolute top-7 left-1/2 -translate-x-1/2 text-xs font-bold text-blue-900 bg-white/90 px-2 py-0.5 rounded-lg border border-blue-200 shadow-sm whitespace-nowrap" data-id="element-9652">
          {labelForTick(computed.min, 0)}
        </div>
      </div>

      {/* End Endpoint */}
      <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/2 z-10" data-id="element-9653">
        <div className="w-5 h-5 rounded-full bg-blue-600 border-2 border-white shadow-lg" data-id="element-9654" />
        <div className="absolute top-7 left-1/2 -translate-x-1/2 text-xs font-bold text-blue-900 bg-white/90 px-2 py-0.5 rounded-lg border border-blue-200 shadow-sm whitespace-nowrap" data-id="element-9655">
          {labelForTick(computed.max, computed.steps)}
        </div>
      </div>

      {/* Ticks and Highlights */}
      {computed.ticks.map((value, i) => {
      const position = i / computed.steps * 100;
      const isHighlighted = approxIncludes(cleanedHighlights, value, computed.epsilon);
      const tickLabel = labelForTick(value, i);
      // Skip rendering the tick dot for endpoints because the large endpoint dots handle them
      const isEndpoint = i === 0 || i === computed.steps;
      // In stimulus mode, hide values for highlighted dots (don't spoil the answer)
      const isStimulusHighlight = mode === 'stimulus' && isHighlighted;
      return <motion.div key={i} initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        delay: i * 0.02,
        type: 'spring',
        stiffness: 400,
        damping: 25
      }} className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2" style={{
        left: `${position}%`
      }} data-id="element-9656">
            {/* Tick Dot (hide for endpoints unless highlighted) */}
            {(!isEndpoint || isHighlighted) && <div className={`rounded-full relative z-10 mx-auto
                ${isHighlighted ? 'w-6 h-6 bg-indigo-600 ring-4 ring-indigo-200 shadow-xl' : 'w-3 h-3 bg-blue-400 shadow-md'}`} data-id="element-9657" />}

            {/* Tick Label (hide for endpoints, hide value for stimulus highlights) */}
            {!isEndpoint && !isStimulusHighlight && <div className={`absolute top-9 left-1/2 -translate-x-1/2 text-xs font-bold whitespace-nowrap
                ${isHighlighted ? 'text-indigo-800' : 'text-slate-500'}`} data-id="element-9658">
                {tickLabel}
              </div>}

            {/* Tooltip for highlights */}
            {isHighlighted && <motion.div initial={{
          opacity: 0,
          y: -8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className={`absolute -top-12 left-1/2 -translate-x-1/2 ${isStimulusHighlight ? 'bg-indigo-500' : 'bg-indigo-600'} text-white px-3 py-1.5 rounded-xl text-sm font-bold shadow-xl whitespace-nowrap z-20`} data-id="element-9659">
                {isStimulusHighlight ? '?' : tickLabel}
                <div className={`absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-transparent ${isStimulusHighlight ? 'border-t-indigo-500' : 'border-t-indigo-600'}`} data-id="element-9660" />
              </motion.div>}
          </motion.div>;
    })}
    </div>;
  // ── STIMULUS MODE: visualization only ─────────────────────────────────────
  if (mode === 'stimulus') {
    return <div className="bg-blue-50 rounded-xl px-6 pt-4 pb-8 border border-blue-200" data-id="element-9661">
        {label && <p className="text-sm font-semibold text-blue-800 mb-3 text-center" data-id="element-9662">
            {label}
          </p>}
        {numberLineViz}
      </div>;
  }
  // ── SUPPORT MODE: full educational panel ──────────────────────────────────
  return <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-2xl border-2 border-blue-200 shadow-sm" data-id="element-9663">
      {label && <div className="mb-8 text-center" data-id="element-9664">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-blue-200 shadow-sm" data-id="element-9665">
            <div className="w-2.5 h-2.5 rounded-full bg-indigo-600 animate-pulse" data-id="element-9666" />
            <span className="text-base font-bold text-blue-900" data-id="element-9667">{label}</span>
          </div>
        </div>}

      <div className="mb-8" data-id="element-9668">{numberLineViz}</div>

      <div className="space-y-4" data-id="element-9669">
        {/* Context-aware explanation */}
        <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-9670">
          <div className="flex items-start gap-3" data-id="element-9671">
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-9672">
              <span className="text-indigo-600 text-lg" data-id="element-9673">📊</span>
            </div>
            <div data-id="element-9674">
              <h4 className="text-sm font-bold text-indigo-900 mb-2" data-id="element-9675">
                {explanationTitle}
              </h4>
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-9676">
                {explanationText}
                {cleanedHighlights.length > 0 && <span className="block mt-2 text-indigo-700 font-semibold" data-id="element-9677">
                    De blauwe punt{cleanedHighlights.length > 1 ? 'en' : ''}{' '}
                    laat zien waar{' '}
                    {cleanedHighlights.map((h, i) => {
                  const closestIdx = computed.ticks.reduce((bestIdx, tick, idx) => Math.abs(tick - h) < Math.abs(computed.ticks[bestIdx] - h) ? idx : bestIdx, 0);
                  return <span key={i} data-id="element-9678">
                          <strong data-id="element-9679">{labelForTick(h, closestIdx)}</strong>
                          {i < cleanedHighlights.length - 1 && (i === cleanedHighlights.length - 2 ? ' en ' : ', ')}
                        </span>;
                })}{' '}
                    {cleanedHighlights.length > 1 ? 'liggen' : 'ligt'}.
                  </span>}
              </p>
            </div>
          </div>
        </div>

        {/* Highlight breakdown */}
        {cleanedHighlights.length > 0 && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-9680">
            <div className="flex items-start gap-3" data-id="element-9681">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-9682">
                <span className="text-emerald-600 text-lg" data-id="element-9683">💡</span>
              </div>
              <div data-id="element-9684">
                <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-9685">
                  Hoe vind je dit getal?
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-9686">
                  {cleanedHighlights.map((h, i) => {
                const stepsFromStart = Math.round((h - computed.min) / computed.stepSize);
                const closestIdx = computed.ticks.reduce((bestIdx, tick, idx) => Math.abs(tick - h) < Math.abs(computed.ticks[bestIdx] - h) ? idx : bestIdx, 0);
                return <span key={i} className="block mb-2 last:mb-0" data-id="element-9687">
                        <strong className="text-emerald-700" data-id="element-9688">
                          {labelForTick(h, closestIdx)}
                        </strong>{' '}
                        ligt{' '}
                        <strong data-id="element-9689">
                          {stepsFromStart}{' '}
                          {stepsFromStart === 1 ? 'stap' : 'stappen'}
                        </strong>{' '}
                        vanaf het begin.
                        {stepsFromStart > 0 && <span className="text-slate-600" data-id="element-9690">
                            {' '}
                            ({labelForTick(computed.min, 0)} + {stepsFromStart}{' '}
                            ×{' '}
                            {formatTick(computed.stepSize, computed.maxFractionDigits)}{' '}
                            = {labelForTick(h, closestIdx)})
                          </span>}
                      </span>;
              })}
                </p>
              </div>
            </div>
          </div>}
      </div>
    </div>;
}